/**
 * Shared file type detection utilities
 * Used across frontend and backend to consistently detect file types
 * and determine which AI processing methods are supported
 */
/**
 * Extract file extension from filename
 *
 * Last segment only (`x.ome.tiff` -> `tiff`); classify with `fileExtensionFromName`.
 */
export const getFileExtension = (fileName) => {
    // A dotless name has no extension. Without this guard `split('.').pop()`
    // returns the whole filename, so `isAudioFile('mp3')` was true and a file
    // literally named `mp3` got routed into the transcription queue.
    if (!fileName.includes('.'))
        return '';
    return fileName.split('.').pop()?.toLowerCase() || '';
};
/**
 * Image file extensions supported for OCR. `avif` is here because every
 * current browser draws it and the OCR path converts it to PNG for the model
 * (`scientific-image-extraction.service.ts`); HEIC and HEIF are kept out on
 * purpose, see `fileKinds.ts`.
 */
export const IMAGE_EXTENSIONS = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'avif',
    'svg',
    'bmp',
    'tiff',
    'tif',
];
/**
 * Audio file extensions supported for transcription.
 *
 * NOTE: ``mp4`` is intentionally absent from the audio list. Standalone audio
 * MP4s arrive with ``audio/mp4`` MIME and are caught by the MIME-prefix check
 * in ``isAudioFile``. The bare ``.mp4`` extension is reserved for video so a
 * ``video/mp4`` upload can never be misclassified as audio (which used to
 * cascade into the transcription queue and fail loudly at the user).
 */
export const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'webm', 'm4a', 'aac', 'flac'];
/**
 * Video file extensions for native ``<video>`` playback.
 */
export const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'avi'];
/**
 * Container extensions that can hold EITHER an audio-only or a
 * video-bearing stream. The extension (and often the browser-supplied MIME —
 * e.g. a phone labels a voice note ``video/webm``) cannot tell them apart, so
 * ``isAudioFile``/``isVideoFile`` can only guess. Callers that need to render
 * the correct player must probe the actual stream (backend ffprobe, or the
 * frontend ``useResolvedMediaKind`` runtime probe) rather than trust the
 * static classification for these.
 */
export const AMBIGUOUS_MEDIA_CONTAINER_EXTENSIONS = ['webm', 'ogg'];
/**
 * Document file extensions supported for OCR or document conversion
 */
export const DOCUMENT_EXTENSIONS = ['pdf', 'docx', 'doc'];
/**
 * Code / script file extensions. Classified by EXTENSION FIRST: most operating
 * systems have no registered MIME type for .R/.Rmd/.jl/.do, so the browser
 * sends '' or application/octet-stream. This is the inverse of isImageFile's
 * MIME-first ordering, and deliberately so.
 */
export const CODE_EXTENSIONS = [
    'r',
    'rmd',
    'rnw',
    'py',
    'ipynb',
    'rb',
    'sql',
    'sh',
    'bash',
    'zsh',
    'jl',
    'm',
    'do',
    'ado',
    'cpp',
    'c',
    'h',
    'hpp',
    'java',
    'js',
    'ts',
    'tsx',
    'jsx',
    'css',
    'yaml',
    'yml',
    'toml',
    'xml',
    'json',
];
/**
 * Whether a file is source code. Extension decides; MIME is ignored because it
 * is unreliable for exactly this class of file (see CODE_EXTENSIONS).
 */
export const isCodeFile = (fileName, _mimeType) => CODE_EXTENSIONS.includes(getFileExtension(fileName));
/**
 * Check if a file is an image based on MIME type or extension
 */
export const isImageFile = (fileName, mimeType) => {
    // First check by MIME type if available
    if (mimeType && mimeType.startsWith('image/')) {
        return true;
    }
    // Fallback to file extension
    const extension = getFileExtension(fileName);
    return IMAGE_EXTENSIONS.includes(extension);
};
/**
 * Check if a file is audio based on MIME type or extension.
 *
 * MIME takes precedence over extension. A ``video/*`` MIME is unambiguous
 * and disqualifies the audio path even if the extension would otherwise
 * match (defensive — used to be triggered by ``video/mp4`` recordings
 * before ``mp4`` was removed from ``AUDIO_EXTENSIONS``).
 */
export const isAudioFile = (fileName, mimeType) => {
    if (mimeType?.startsWith('audio/')) {
        return true;
    }
    if (mimeType?.startsWith('video/')) {
        return false;
    }
    return AUDIO_EXTENSIONS.includes(getFileExtension(fileName));
};
/**
 * Check if a file is video based on MIME type or extension.
 *
 * Mirrors ``isAudioFile``: ``audio/*`` MIME excludes from the video path
 * even if the extension matches.
 */
export const isVideoFile = (fileName, mimeType) => {
    if (mimeType?.startsWith('video/')) {
        return true;
    }
    if (mimeType?.startsWith('audio/')) {
        return false;
    }
    return VIDEO_EXTENSIONS.includes(getFileExtension(fileName));
};
/**
 * Whether a media file is stored in a container whose extension/MIME cannot
 * disambiguate audio from video (webm, ogg).
 *
 * ``true`` means the static ``isAudioFile``/``isVideoFile`` result is a guess:
 * a ``.webm`` classifies as audio by extension yet may carry a video stream,
 * and a browser-labelled ``video/webm`` voice note carries no video at all.
 * Both the extension and a container-only MIME subtype trigger this — the
 * latter catches renamed files that lost their extension.
 */
export const isAmbiguousMediaContainer = (fileName, mimeType) => {
    const extension = getFileExtension(fileName);
    if (AMBIGUOUS_MEDIA_CONTAINER_EXTENSIONS.includes(extension)) {
        return true;
    }
    const subtype = mimeType?.split('/')[1]?.toLowerCase();
    return subtype === 'webm' || subtype === 'ogg';
};
/**
 * Check if a file is a PDF based on MIME type or extension
 */
export const isPdfFile = (fileName, mimeType) => {
    // First check by MIME type if available
    if (mimeType === 'application/pdf') {
        return true;
    }
    // Fallback to file extension
    const extension = getFileExtension(fileName);
    return extension === 'pdf';
};
/**
 * Check if a file is a DOCX based on MIME type or extension
 */
export const isDocxFile = (fileName, mimeType) => {
    // First check by MIME type if available
    if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mimeType === 'application/msword') {
        return true;
    }
    // Fallback to file extension
    const extension = getFileExtension(fileName);
    return extension === 'docx' || extension === 'doc';
};
/**
 * Check if a file supports OCR (Optical Character Recognition)
 * Supported: Images only
 */
export const supportsOCR = (fileName, mimeType) => {
    return isImageFile(fileName, mimeType);
};
/**
 * Check if a file supports transcription (Speech-to-Text)
 * Supported: Audio files
 */
export const supportsTranscription = (fileName, mimeType) => {
    return isAudioFile(fileName, mimeType);
};
/**
 * Check if a file supports DOCX conversion to TipTap format
 * Supported: DOCX files only
 */
export const supportsDocxConversion = (fileName, mimeType) => {
    return isDocxFile(fileName, mimeType);
};
/**
 * Check if a file supports PDF conversion to TipTap format
 * Supported: PDF files only
 */
export const supportsPdfConversion = (fileName, mimeType) => {
    return isPdfFile(fileName, mimeType);
};
/**
 * Check if a file is plain text or markdown (no LLM extraction needed)
 */
export const isTextFile = (fileName, mimeType) => {
    if (mimeType === 'text/plain' || mimeType === 'text/markdown' || mimeType === 'text/x-markdown') {
        return true;
    }
    const ext = getFileExtension(fileName);
    return ext === 'txt' || ext === 'md';
};
/**
 * Check if a file supports AI content extraction (OCR, transcription, DOCX conversion, or PDF conversion).
 * Used to determine if an attachment will have its markdown enriched after upload.
 */
export const supportsAIExtraction = (fileName, mimeType) => {
    return (supportsOCR(fileName, mimeType) ||
        supportsTranscription(fileName, mimeType) ||
        supportsDocxConversion(fileName, mimeType) ||
        supportsPdfConversion(fileName, mimeType));
};
/**
 * Check if a file should be converted into a note during batch import.
 *
 * PDFs are intentionally excluded — they stay as attachments with extracted markdown
 * so the original PDF viewer is preserved. Only files whose content is best consumed
 * as editable text (DOCX, images, audio) become notes.
 *
 * Derived from getProcessingType so it stays in sync with FILE_PROCESSING_CONFIG.
 *
 * NOT the gate used by `note-creation.service.ts`, and that is deliberate:
 * this helper answers "should BATCH IMPORT auto-convert this?", and its `pdf`
 * exclusion is part of that answer. That service is the backing path for
 * MANUALLY converting a PDF to a note, so gating it here would refuse the one
 * case it exists to serve (15 scenarios in
 * `backend/.../ai-note-create-workflow.service.test.ts` pin it). It checks
 * `getProcessingType(...) === 'code'` directly instead.
 */
export const supportsNoteConversion = (fileName, mimeType) => {
    const type = getProcessingType(fileName, mimeType);
    // Code files stay attachments so the byte-identical file round-trips back
    // to the researcher's tool (RStudio, etc.) — never auto-converted to a note.
    // Video for the same reason manual conversion refuses it (no extraction
    // pipeline), and because FILE_PROCESSING_CONFIG.video is `autoConvert:
    // 'never'` — which this helper claims to stay in sync with and did not.
    // Nothing in production calls this today (the live auto-convert path is
    // dispatchPostUpload, which returns early for video), so this is closing the
    // sibling before a future caller reopens the bug, not fixing a live one.
    return type !== null && type !== 'pdf' && type !== 'code' && type !== 'video';
};
/**
 * Can this processing type be MANUALLY converted to a note?
 *
 * The gate for the "Convert to Note" action and for the service behind it
 * (`note-creation.service.ts`). Takes an already-resolved type so the service,
 * which needs `processingType` for a dozen other branches, shares this
 * definition without resolving twice.
 *
 * Distinct from `supportsNoteConversion` above, and the difference is the
 * whole point: that one answers "should BATCH IMPORT auto-convert this?" and
 * therefore also excludes `pdf`. A PDF is exactly the case manual conversion
 * exists to serve. Gating the menu on the batch-import helper silently removed
 * the action for every PDF — the warning two docblocks up describes that
 * mistake, and it was made anyway (caught in review, 2026-08-14), so the two
 * predicates now live side by side with one name each.
 *
 * Spelled as an EXHAUSTIVE table rather than the `!== 'code'` denylist it used
 * to be, because the denylist is how `video` got in. `video` joined
 * `getProcessingType`'s union in #119; this predicate was written later and
 * admitted it by default, so the menu offered conversion for a type
 * `extraction.service.extractFull` short-circuits before its dispatch switch.
 * Conversion then wrote a note whose entire body was "Failed to process file";
 * a customer hit that on an .mp4 on 2026-08-18, abandoned the feature and
 * re-keyed the content by hand.
 *
 * What is missing is the DISPATCH, not a pipeline: the switch `extractFull`
 * skips would route video to `runAudioVideo` → `transcriptionService`, and
 * those providers already take `video/webm|mp4|mpeg` on purpose (the segmenter
 * strips the video stream with ffmpeg `-vn`, and both `POST /attachments/:id/
 * transcribe` and the Bird `transcribe_attachment` tool feed video in today).
 * So flipping `video` here is a decision about quota, duration and cost —
 * roughly the size of wiring that one case, not of building transcoding.
 *
 * Adding a member to `getProcessingType`'s return union is a COMPILE ERROR
 * against this table until someone answers the question for it, so the default
 * stops being "convertible, probably". (Keyed on that union rather than
 * `ProcessingTypeKey`, which is wider — this predicate only ever receives
 * `getProcessingType` output.)
 */
const MANUALLY_CONVERTIBLE_TO_NOTE = {
    ocr: true,
    transcription: true,
    docx: true,
    pdf: true,
    text: true,
    /** Stays a file so the bytes round-trip to RStudio / Stata / MATLAB. */
    code: false,
    /** No extraction pipeline behind it — see the docblock above. */
    video: false,
};
export const canConvertProcessingTypeToNote = (type) => type !== null && MANUALLY_CONVERTIBLE_TO_NOTE[type];
/** File-name convenience over {@link canConvertProcessingTypeToNote}. */
export const canConvertToNote = (fileName, mimeType) => canConvertProcessingTypeToNote(getProcessingType(fileName, mimeType));
/**
 * Get the processing type for a file.
 *
 * Returns one of the non-fallback ProcessingTypeKeys, or null for unsupported types.
 * null maps to 'other' in getFileConfig() — no extraction, filename title.
 *
 * Ordered by specificity: video/transcription/docx/pdf/code/text/ocr/null.
 * Video is checked BEFORE transcription so a ``video/mp4`` doesn't
 * accidentally route through the audio-only transcription path. ``code`` is
 * checked BEFORE ``text`` — deliberately, and NOT for symmetry with the other
 * checks: `isCodeFile` is extension-only (browsers/OSes routinely have no
 * registered MIME for .py/.R/.sh, and even when they do, upload's own
 * `octetStreamMap` remaps ts/py/yaml/yml/sh to `text/plain` before this ever
 * runs), while `isTextFile` has a MIME-first branch. Checking `text` first
 * let a `text/plain`-labelled `.py` win the `text` branch — which auto-
 * converts to a note (`FILE_PROCESSING_CONFIG.text.autoConvert === 'always'`)
 * before the file ever gets its language-tagged fence. `CODE_EXTENSIONS`
 * doesn't include `md`/`txt`, so `.md`/`.txt` still resolve to `text`
 * unchanged by this ordering. Checked BEFORE the OCR/null tail so code
 * extensions never fall through to 'other'.
 */
export const getProcessingType = (fileName, mimeType) => {
    if (isVideoFile(fileName, mimeType)) {
        return 'video';
    }
    if (supportsTranscription(fileName, mimeType)) {
        return 'transcription';
    }
    if (supportsDocxConversion(fileName, mimeType)) {
        return 'docx';
    }
    if (supportsPdfConversion(fileName, mimeType)) {
        return 'pdf';
    }
    if (isCodeFile(fileName, mimeType)) {
        return 'code';
    }
    if (isTextFile(fileName, mimeType)) {
        return 'text';
    }
    if (supportsOCR(fileName, mimeType)) {
        return 'ocr';
    }
    return null;
};
//# sourceMappingURL=fileTypeDetection.js.map